import { DEFAULT_PAPER_WIDTH } from '@constants';
import { createTheme, CSSObject, lighten, Theme } from '@mui/material';
import { deepmerge } from '@mui/utils';

import { BLS_COLORS, ColorPalette, PROBONIO_COLORS } from './colors';

export interface AppTheme {
  name: string;
  colors: ColorPalette;
  theme: ReturnType<typeof createTheme>;
}

type StyleOverridesProps<P> = { theme: Theme; ownerState: P };

function mergeStyleOverrides<P>(
  base: CSSObject | ((props: StyleOverridesProps<P>) => CSSObject) | unknown,
  overrides: CSSObject | ((props: StyleOverridesProps<P>) => CSSObject),
): (props: StyleOverridesProps<P>) => CSSObject {
  const baseStyles = typeof base === 'function' ? base : () => base ?? {};
  const overrideStyles = typeof overrides === 'function' ? overrides : () => overrides;

  return (props: StyleOverridesProps<P>) => deepmerge(baseStyles(props), overrideStyles(props));
}

const BASE_THEME = (COLORS: ColorPalette) =>
  createTheme({
    cssVariables: true,
    palette: {
      mode: 'light',
      primary: {
        main: COLORS.PRIMARY,
      },
      background: {
        default: COLORS.BACKGROUNDS.DEFAULT,
        paper: COLORS.BACKGROUNDS.PAPER,
      },
      text: {
        primary: COLORS.TEXT,
      },
      divider: COLORS.BORDER,
      error: {
        main: COLORS.ERROR,
      },
      warning: {
        main: COLORS.WARNING,
      },
    },
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            width: '100%',
            '& .MuiAutocomplete-inputRoot': {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              '& .MuiAutocomplete-endAdornment': {
                display: 'flex',
                justifyContent: 'flex-end',
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                maxWidth: '0.01px',
              },
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              backgroundColor: 'transparent',
              border: 'none',
              color: 'currentColor',
              height: 28,
              width: 28,
              '&:hover': { backgroundColor: 'transparent' },
            },
          },
          paper: {
            padding: 0,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            letterSpacing: 0,
            fontFamily: 'DM Sans Variable, sans-serif',
          },
          caption: {
            fontSize: 12,
            lineHeight: '18px',
          },
          h5: {
            fontSize: 24,
            fontWeight: 600,
          },
          h6: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            overflow: 'hidden',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: DEFAULT_PAPER_WIDTH,
            padding: 24,
            borderRadius: 8,
            border: `1px solid ${COLORS.BORDER}`,
            boxShadow: 'none',
            [theme.breakpoints.only('xs')]: {
              width: '100%',
              maxWidth: 392,
            },
            [theme.breakpoints.only('sm')]: {
              width: '100%',
              maxWidth: DEFAULT_PAPER_WIDTH,
              transform: 'scale(1.125)',
            },
            [theme.breakpoints.up('lg')]: {
              maxHeight: 800,
              overflowY: 'auto',
            },
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paperWidthMd: ({ theme }) => ({
            padding: 40,
            maxWidth: 800,
            overflowY: 'hidden',
            [theme.breakpoints.only('sm')]: {
              transform: 'none',
            },
          }),
        },
      },
      MuiStack: {
        styleOverrides: {
          root: {
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          icon: false,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            width: '100%',
            maxWidth: '100%',
            color: COLORS.TEXT,
            fontSize: 16,
            fontFamily: 'DM Sans Variable, sans-serif',
            letterSpacing: 0,
            lineHeight: '24px',
            [theme.breakpoints.only('sm')]: {
              transform: 'none',
            },
            [theme.breakpoints.only('xs')]: {
              width: 'unset',
              maxWidth: 'unset',
            },
            '&.MuiAlert-standard.MuiAlert-colorError': {
              borderColor: COLORS.ERROR,
              backgroundColor: lighten(COLORS.ERROR, 0.8),
            },
            '&.MuiAlert-standard.MuiAlert-colorWarning': {
              borderColor: COLORS.WARNING,
              backgroundColor: lighten(COLORS.WARNING, 0.8),
            },
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          variant: 'outlined',
          slotProps: { inputLabel: { shrink: true } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            height: 36,
            borderRadius: 4,
            backgroundColor: 'transparent',
            '& input': {
              fontFamily: 'DM Sans Variable, sans-serif',
              fontSize: 14,
              letterSpacing: 0,
              lineHeight: '18px',
              padding: '9px 8px',
              // iOS Safari auto-zooms into a focused input whose font-size is < 16px,
              // scaling the whole page and introducing a phantom horizontal scroll
              // (the autofocused registration-code field triggers this on load).
              // Keep the 14px design on desktop but use 16px at mobile widths to suppress it.
              '@media (max-width: 900px)': {
                fontSize: 16,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.BORDER,
            },
            '& .MuiOutlinedInput-notchedOutline legend span': {
              display: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.BORDER,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: COLORS.FOCUS,
            },
            '&.Mui-disabled': {
              backgroundColor: COLORS.BACKGROUNDS.DISABLED,
              '& input': {
                color: COLORS.DISABLED_TEXT,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.BORDER,
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 16,
            fontWeight: 400,
            fontFamily: 'DM Sans Variable, sans-serif',
            letterSpacing: 0,
            lineHeight: '100%',
            color: COLORS.TEXT,
            width: 'max-content',
            position: 'unset',
            marginBottom: 4,
            transform: 'translate(0, 0)',
            '&.Mui-focused': {
              color: COLORS.TEXT,
            },
            '&.Mui-disabled': {
              color: COLORS.TEXT,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 2,
            color: COLORS.TEXT,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            height: 48,
            width: '100%',
            borderRadius: 4,
            fontSize: 16,
            fontWeight: 900,
            fontFamily: 'DM Sans Variable, sans-serif',
            textTransform: 'none',
            '&:focus-visible': {
              outline: `2px solid ${COLORS.FOCUS}`,
              outlineOffset: '4px',
            },
          },
          contained: {
            color: COLORS.BUTTON_TEXT,
            backgroundColor: COLORS.BACKGROUNDS.BUTTON,
            '&:hover': {
              backgroundColor: COLORS.HOVER,
            },
            '&:disabled': {
              backgroundColor: COLORS.BORDER,
            },
          },
          outlined: {
            color: COLORS.TEXT,
            backgroundColor: 'transparent',
            border: `1px solid ${COLORS.BACKGROUNDS.BUTTON}`,
            '&:hover': {
              color: COLORS.HOVER,
              borderColor: COLORS.HOVER,
            },
            '&:disabled': {
              borderColor: COLORS.BORDER,
              backgroundColor: 'transparent',
            },
          },
          text: {
            padding: 0,
            width: 'auto',
            height: 'auto',
            fontWeight: 400,
            color: COLORS.LINK,
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            height: 50,
            width: '100%',
            color: COLORS.TEXT,
            borderRadius: 4,
            border: `1px solid ${COLORS.BORDER}`,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: COLORS.HOVER,
            },
            '&:disabled': {
              backgroundColor: COLORS.BORDER,
            },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.FOCUS}`,
              outlineOffset: '4px',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            width: '100%',
            '& .MuiTypography-root': {
              fontFamily: 'DM Sans Variable, sans-serif',
            },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
        styleOverrides: {
          root: {
            lineHeight: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            color: COLORS.LINK,
            fontFamily: 'DM Sans Variable, sans-serif',
            '&:focus-visible': {
              textDecoration: 'underline',
              outline: '2px solid currentColor',
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            alignSelf: 'baseline',
            padding: 0,
            marginRight: 8,
            '&.Mui-focusVisible::after': {
              content: '""',
              inset: -6,
              position: 'absolute',
              pointerEvents: 'none',
              borderRadius: 4,
              border: `2px solid ${COLORS.FOCUS}`,
            },
            '&.Mui-checked': {
              color: COLORS.LINK,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            margin: 0,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            position: 'relative',
            '::before': {
              content: '""',
              borderBottomWidth: 3,
              borderBottomColor: COLORS.BORDER,
              borderBottomStyle: 'solid',
              borderRadius: 2,
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
            },
          },
        },
        defaultProps: {
          variant: 'fullWidth',
          slotProps: {
            indicator: {
              sx: {
                borderRadius: 2,
                height: 3,
                backgroundColor: COLORS.PRIMARY_DARK,
              },
            },
          },
        },
      },
      MuiTab: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: 'DM Sans Variable, sans-serif',
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: 0,
            lineHeight: '24px',
            color: COLORS.TEXT,
            '&.Mui-selected': {
              color: COLORS.TEXT,
            },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.FOCUS}`,
              outlineOffset: '-8px',
            },
          },
        },
      },
    },
  });

export function PROBONIO_THEME(): AppTheme {
  const COLORS = PROBONIO_COLORS;
  const baseTheme = BASE_THEME(COLORS);

  return {
    name: 'Probonio',
    colors: COLORS,
    theme: createTheme(baseTheme),
  };
}

export function BLS_THEME(): AppTheme {
  const COLORS = BLS_COLORS;
  const baseTheme = BASE_THEME(COLORS);

  return {
    name: 'BLS',
    colors: COLORS,
    theme: createTheme(baseTheme, {
      components: {
        MuiPaper: {
          styleOverrides: {
            root: mergeStyleOverrides(baseTheme.components?.MuiPaper?.styleOverrides?.root, ({ theme }) => ({
              [theme.breakpoints.down('md')]: {
                border: 'none',
                boxShadow: '0px 0px 30px 0px rgba(0,0,0,0.08)',
              },
            })),
          },
        },
      },
    }),
  };
}
